import { batchUpdateCareMessagesAPI } from '@/api/care';
import { careStatusOptions } from '@/utils/careScheduleStatus';

// 使用此流程的列表須提供 loading 與 fetchData()；更新後一律重讀該列表。
export default {
  data() {
    return {
      saving: false, editDialog: false, currentItem: null, editableMessages: [],
      datePickerMenus: [], minDate: '', statusOptions: careStatusOptions,
    };
  },
  methods: {
    isEditable(item) {
      if (!item.messages || item.messages.length === 0) {
        return false;
      }
      return item.messages.some(msg => this.isMessageEditable(msg.send_status));
    },
    isMessageEditable(status) {
      return ['0', '2', '3', '4', '5'].includes(String(status));
    },
    statusOptionsFor(msg) {
      const allowed = msg.originalStatus === '4' ? ['3'] : ['0', '3'];
      return this.statusOptions.filter(option => allowed.includes(option.value) || option.value === msg.originalStatus);
    },
    isTimeDisabled(msg) {
      return this.saving || msg.originalStatus === '4' || msg.status === '3';
    },
    statusColor(status) {
      switch(String(status)) {
        case '0': return 'blue-6';
        case '1': return 'green-6';
        case '2': return 'red-6';
        case '3': return 'grey-5';
        case '4': return 'orange-6';
        case '5': return 'red-6';
        default: return 'grey-5';
      }
    },
    formatStatus(status) {
      const found = this.statusOptions.find(option => option.value === String(status));
      return found ? found.text : '未知';
    },
    openEditDialog(item) {
      if (this.loading || this.saving || this.readOnly) return;
      this.currentItem = item;
      this.minDate = this.$moment().tz('Asia/Taipei').format('YYYY-MM-DD');
      this.editableMessages = item.messages.map((msg, index) => {
        const taipeiTime = this.$moment(msg.scheduled_send_at).tz('Asia/Taipei');

        return {
          id: msg.id,
          title: msg.title || `細項 ${index + 1}`,
          date: taipeiTime.format('YYYY-MM-DD'),
          hour: taipeiTime.format('HH'),
          minute: taipeiTime.format('mm'),
          status: String(msg.send_status),
          originalStatus: String(msg.send_status),
          originalTime: taipeiTime.format('YYYY-MM-DDTHH:mm'),
          originalScheduledSendAt: msg.scheduled_send_at,
          isPotentiallyEditable: this.isMessageEditable(msg.send_status),
          isEditing: false,
        };
      });
      this.datePickerMenus = Array(item.messages.length).fill(false);
      this.editDialog = true;
    },
    toggleEditMessage(msg) {
      if (this.saving || !msg.isPotentiallyEditable) return;
      msg.isEditing = !msg.isEditing;
    },
    closeEditDialog() {
      this.editDialog = false;
      this.currentItem = null;
      this.editableMessages = [];
      this.datePickerMenus = [];
    },
    async saveChanges() {
      if (this.saving || !this.editDialog || this.readOnly) return;
      const updates = [];
      for (const msg of this.editableMessages) {
        if (!msg.isEditing || !msg.isPotentiallyEditable) continue;
        const statusChanged = msg.status !== msg.originalStatus;
        const localTime = `${msg.date}T${msg.hour}:${msg.minute}`;
        // 只提交變更列；每列仍需完整 ID、時間、狀態，取消保留原時間。
        const timeChanged = !this.isTimeDisabled(msg) && localTime !== msg.originalTime;
        if (!statusChanged && !timeChanged) continue;
        if (!this.statusOptionsFor(msg).some(option => option.value === msg.status)) {
          this.$notify.warning('請選擇可用的發送狀態。');
          return;
        }
        const sendTime = timeChanged
          ? this.$moment.tz(localTime, 'YYYY-MM-DDTHH:mm', true, 'Asia/Taipei')
          : this.$moment(msg.originalScheduledSendAt);
        if (!/^[1-9]\d*$/.test(String(msg.id)) || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(msg.originalScheduledSendAt || '') || !sendTime.isValid()) {
          this.$notify.warning('排程 ID 或發送時間無效，請重新查詢。');
          return;
        }
        if (timeChanged && (!/^\d{2}$/.test(msg.minute) || Number(msg.minute) % 5 !== 0)) {
          this.$notify.warning('發送時間請使用五分鐘刻度。');
          return;
        }
        if (timeChanged || (statusChanged && msg.status === '0')) {
          if (!sendTime.isValid() || !sendTime.isAfter(this.$moment())) {
            this.$notify.warning('請將發送時間設定為有效的未來時間。');
            return;
          }
        }
        updates.push({
          id: msg.id,
          scheduled_send_at: timeChanged ? sendTime.utc().format() : msg.originalScheduledSendAt,
          send_status: timeChanged ? 0 : Number(msg.status),
        });
      }

      if (updates.length === 0) {
        this.$notify.warning('沒有已變更的項目可儲存。');
        return;
      }

      this.saving = true;
      try {
        await batchUpdateCareMessagesAPI({ updates });
        this.$notify.success('更新成功！');
      } catch (error) {
        const data = error.response && error.response.data;
        const validationErrors = data && data.errors && Object.values(data.errors).flat();
        const reason = (validationErrors && validationErrors.find(message => typeof message === 'string'))
          || (data && typeof data.message === 'string' && data.message)
          || (data && typeof data.messages === 'string' && data.messages)
          || '無法確認排程更新結果。';
        this.$notify.error(`${reason} 部分項目可能已更新，請依重新查詢的結果確認。`);
      } finally {
        // 批次更新可能部分成功；清除舊編輯資料，避免直接重送同一批。
        this.closeEditDialog();
        await this.fetchData();
        this.saving = false;
      }
    },
  },
};
